import { DownloadItemDataModal } from './DownloadItemDataModal'
import { useEffect, useState, useMemo } from 'react'
import { MODES } from '../../../constants/downloadConstants'
import { userIsInSomeRole, getUserInfo } from '../../../resources/commonUtills'
import { AgGridReact } from 'ag-grid-react'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { LicenseManager } from '@ag-grid-enterprise/core'
import { Button, Card, Grid, Heading } from '@enterprise-ui/canvas-ui-react'
import { useSelector } from 'react-redux'
import PaginationControls from '../gridFooter/Pagination'
import { GridHeader } from '../gridHeader'
import { useSearchGrid } from './useSearchGrid'
import { useGridHeader } from '../gridHeader/useGridHeader' // React Grid Logic
import { useGridDownload } from '../gridHeader/useGridDownload'
import { Actionbar } from '../actionbar'
import { SearchHeader } from '../header'
import getTapConfig from '../../../config/tapConfig'
import EnterpriseIcon, { UploadIcon } from '@enterprise-ui/icons'
import { AG_GRID_LICENSE_KEY } from '../../../constants/ag-grid'
import { useUploadMfe } from './useUploadMfe'
import constant from '../../../constants/constants'
import apiConfig from '../../../config'
LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY)

export const SearchGrid = () => {
  const {
    searchItems,
    loading,
    gridColumns,
    gridOptions,
    onBtFirst,
    onBtLast,
    onBtNext,
    onBtPrevious,
    onBtSpecificPage,
    onPaginationChanged,
    gridApi,
    getCurrentPage,
    paginationPageSizeValue,
    getTotalPage,
    openCloseToolPanel,
    maximize,
    setMaximize,
    selectedItems,
    onGridReady,
    handlePaginationPageSizeChange,
    onSelectionChanged,
    onRowSelected,
    getRowId,
    isExpanded,
    setIsExpanded,
    getFilterVariables,
  } = useSearchGrid()
  const { sideBar } = useGridHeader()
  const { handleUploadClick, uploadModal } = useUploadMfe()
  const { handleDownloadSelectedItems, downloadInProgress } = useGridDownload()
  const getTotalResult = gridApi?.current?.api?.getDisplayedRowCount()
  const userProfile = useSelector((state) => {
    return getUserInfo(state)
  })
  const isInternalUserForBulk = userIsInSomeRole(
    userProfile?.roles,
    constant.USER_ROLE_GROUPINGS.INTERNAL_USER_BULK,
  )
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [downloadContext, setDownloadContext] = useState(null)
  const veItemBulkBase = `${apiConfig.remotes.bulkX}/remoteEntry.js`
  const selectedItemsForDownload = useMemo(
    () =>
      (selectedItems?.filter((item) => item?.tcin) || []).map((item) => ({
        ...item,
        itemTypeLabel: item?.itemTypeName || '',
        itemTypeId: item?.itemTypeId || '',
      })),
    [selectedItems],
  )
  const selectedTcins = useMemo(
    () => selectedItems?.map((item) => item?.tcin).filter(Boolean),
    [selectedItems],
  )
  const startItemIndex = (getCurrentPage - 1) * paginationPageSizeValue + 1
  let endItemIndex = getCurrentPage * paginationPageSizeValue
  endItemIndex = Math.min(endItemIndex, getTotalResult)
  const displayText = `Showing ${startItemIndex}-${endItemIndex} of ${getTotalResult}`
  const classNames = [
    'ag-theme-quartz hc-pb-none search-grid',
    !maximize && 'search-grid-min',
    selectedItems.length > 0 && 'with-action',
    selectedItems.length === 0 && 'with-no-action',
    isExpanded && 'sticker-expanded',
    !isExpanded && 'sticker-collapsed',
  ]
    .filter(Boolean)
    .join(' ')

  const [actionControls, setActionControls] = useState({
    searchActionButtons: {
      casepackEnable: false,
      vopEnable: false,
      presentationEnable: false,
    },
  })
  useEffect(() => {
    getTapConfig().then((appConfig) => {
      setActionControls(appConfig)
    })
  }, [])

  return (
    <>
      {uploadModal}
      <div className="top-right hc-ml-normal">
        {isInternalUserForBulk && (
          <Grid.Container justify="space-between" className="hc-pb-md">
            <Grid.Item>
              <Heading>Item Search</Heading>
            </Grid.Item>
            <Grid.Item>
              <Button type="secondary" onClick={() => handleUploadClick()}>
                <EnterpriseIcon icon={UploadIcon} size="md" />
                Upload item data
              </Button>
            </Grid.Item>
          </Grid.Container>
        )}
        <Card className="hc-mt-none hc-mb-normal">
          <SearchHeader setIsExpanded={setIsExpanded} isExpanded={isExpanded} />
        </Card>
        <Card
          className={`${maximize ? 'lp-is-fullscreen' : ''} hc-mt-none hc-mb-normal `}
        >
          <GridHeader
            openCloseToolPanel={openCloseToolPanel}
            getTotalResult={getTotalResult}
            setMaximize={setMaximize}
            maximize={maximize}
            loading={loading}
            gridApi={gridApi}
            selectedItems={selectedItems}
            isInternalUserForBulk={isInternalUserForBulk}
            onDownloadItemData={({ itemTypeCounts }) => {
              setDownloadContext({
                mode: MODES.ALL_ITEMS,
                totalCount: getTotalResult,
                itemTypeCounts,
                searchCriteria: getFilterVariables(),
              })
              setIsDownloadModalOpen(true)
            }}
          />
          <Grid.Container justify="center">
            <Grid.Item className={classNames}>
              <AgGridReact
                ref={gridApi}
                rowData={searchItems}
                gridOptions={gridOptions}
                columnDefs={gridColumns}
                pagination={true}
                onGridReady={onGridReady}
                paginationPageSize={paginationPageSizeValue}
                paginationPageSizeSelector={[50, 75, 100]}
                onPaginationChanged={onPaginationChanged}
                suppressPaginationPanel={true}
                modules={[ServerSideRowModelModule, ColumnsToolPanelModule]}
                sideBar={sideBar}
                getRowId={getRowId}
                onSelectionChanged={onSelectionChanged}
                onRowSelected={onRowSelected}
              />
            </Grid.Item>
            {!loading && gridColumns && getTotalResult > 0 && (
              <Grid.Item style={{ width: '99%' }} className="hc-pt-none">
                <Actionbar
                  gridApi={gridApi}
                  selectedItems={selectedItems}
                  actionControls={actionControls}
                  onOpenDownloadModal={() => {
                    setDownloadContext({
                      mode: MODES.SELECTED_ITEMS,
                      totalCount: selectedItems.length,
                      searchCriteria: getFilterVariables(),
                    })
                    setIsDownloadModalOpen(true)
                  }}
                  onExportVisibleColumns={
                    isInternalUserForBulk
                      ? (e) => {
                          handleDownloadSelectedItems(e, gridApi?.current?.api)
                        }
                      : undefined
                  }
                  exportInProgress={
                    isInternalUserForBulk ? downloadInProgress : false
                  }
                />
              </Grid.Item>
            )}
          </Grid.Container>
        </Card>
        <div className="grid-pagination-container hc-pb-min">
          <PaginationControls
            getTotalPage={getTotalPage}
            getCurrentPage={getCurrentPage}
            onBtLast={onBtLast}
            onBtNext={onBtNext}
            onBtSpecificPage={onBtSpecificPage}
            onBtPrevious={onBtPrevious}
            onBtFirst={onBtFirst}
            paginationPageSizeValue={paginationPageSizeValue}
            handlePaginationPageSizeChange={handlePaginationPageSizeChange}
            loading={loading}
            displayText={displayText}
          />
        </div>
      </div>
      <DownloadItemDataModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        downloadContext={downloadContext}
        src={veItemBulkBase}
        selectedTcins={selectedTcins}
        selectedItems={selectedItemsForDownload}
        selectedItemCount={selectedItems.length}
      />
    </>
  )
}
